import { CharDisplay } from "./charStream";
import { Direction, Party, SegmentStatus, StreamState, isForeignLang } from "./types";

export type Accent = "caller" | "agent" | "interpreter";
export type Stripe = "left" | "right";

export interface SegmentLine {
  segId: string;
  english: string;
  original: string;
  lang: string;
  active: boolean;
  status: SegmentStatus;
}

export interface CardView {
  id: string;
  party: Party;
  label: string;
  accent: Accent;
  stripe: Stripe;
  direction: Direction;
  aiInterpreter: boolean;
  createdAt?: string | number;
  historical: boolean;
  active: boolean;
  finalized: boolean;
  segments: SegmentLine[];
  english: string;
  original: string;
  /** Uppercased 2-letter badge, empty when the card needs no source-language row. */
  lang: string;
  /**
   * Raw language the card's speech was transcribed in ("" when the provider sent
   * none). Unlike `lang` this is never blanked for presentation, so consumers can
   * tell an English turn from a foreign one on any leg — e.g. the agent speaking
   * for themselves vs. a human interpreter relaying on the same agent leg.
   */
  sourceLang: string;
  englishStreaming: boolean;
  originalStreaming: boolean;
}

const normForCompare = (s: string): string =>
  s.toLowerCase().replace(/[\s.,!?¿¡;:'"“”‘’()\[\]{}\-–—_/\\]+/g, "");

// A previous utterance is treated as "complete" when it ends in sentence
// punctuation (incl. trailing quotes/brackets). Spanish ¿/¡ are openers, so they
// don't count as endings.
const SENTENCE_END = /[.!?…]["'”’)\]]*$/;

/**
 * Join consecutive same-speaker utterances for one card. A line break is added
 * between two parts ONLY when the earlier part ended a sentence — i.e. a true
 * utterance boundary. When it did not (the ASR echo split ONE sentence across
 * finals, e.g. "reducing the" + "traffic flow?"), the parts are a continuation
 * and are joined with a space so the sentence is not broken mid-way. Because the
 * break is derived from the reconciled parts (pushCumulative already collapses a
 * single streaming utterance into one part), there is exactly one break per
 * boundary and never a duplicate. A speaker change never reaches here — it lives
 * in a different card.
 */
function joinUtterances(parts: string[]): string {
  let out = "";
  for (const part of parts) {
    if (!out) {
      out = part;
      continue;
    }
    out += (SENTENCE_END.test(out.trimEnd()) ? "\n" : " ") + part;
  }
  return out;
}

function pushCumulative(parts: string[], piece: string): void {
  const p = piece.trim();
  if (!p) return;
  if (parts.length === 0) {
    parts.push(p);
    return;
  }
  const last = parts[parts.length - 1];
  const a = normForCompare(last);
  const b = normForCompare(p);
  if (!b) return;
  if (a === b) return;
  if (b.startsWith(a)) {
    parts[parts.length - 1] = p;
    return;
  }
  if (a.startsWith(b)) return;
  parts.push(p);
}

const stripeForDirection = (direction: Direction, party: Party): Stripe => {
  if (direction === "outbound") return "right";
  if (direction === "inbound") return "left";
  return party === "agent" ? "right" : "left";
};

const labelFor = (party: Party, aiInterpreter: boolean): string => {
  if (party === "caller") return "Caller";
  if (party === "agent") return "Agent";
  return aiInterpreter ? "AI Interpreter" : "Interpreter";
};

export function toCards(
  state: StreamState,
  display: Record<string, CharDisplay>,
  animatingId?: string | null
): CardView[] {
  const activeSeg = animatingId ?? state.activeSegId;
  const cards: CardView[] = [];
  for (const cardId of state.cardOrder) {
    const card = state.cards[cardId];
    if (!card) continue;

    const lines: SegmentLine[] = [];
    let anyActive = false;
    let allFinalized = true;
    const englishParts: string[] = [];
    const originalParts: string[] = [];
    let cardLang = "";
    let englishStreaming = false;
    let originalStreaming = false;

    for (const segId of card.segIds) {
      const seg = state.segments[segId];
      if (!seg) continue;
      const disp = display[segId] || { rolling: seg.rolling, interp: seg.interp };
      const foreign = isForeignLang(seg.sourceLang);

      const english = disp.interp;
      const original = foreign ? disp.rolling : "";
      const showSecondary =
        foreign && !!original.trim() && original.trim() !== english.trim();

      if (!english.trim() && !original.trim()) continue;

      const active = activeSeg === segId;
      if (active) anyActive = true;
      if (!seg.finalized) allFinalized = false;

      const lang = showSecondary
        ? (seg.sourceLang || "").toString().substring(0, 2).toUpperCase()
        : "";
      const badge = lang && lang !== "EN" ? lang : "";
      if (badge && !cardLang) cardLang = badge;

      lines.push({
        segId,
        english,
        original: showSecondary ? original : "",
        lang: badge,
        active,
        status: seg.status
      });

      if (english.trim()) {
        pushCumulative(englishParts, english);
        if (active) englishStreaming = true;
      }
      if (showSecondary) {
        pushCumulative(originalParts, original);
        if (active && !english.trim()) originalStreaming = true;
      }
    }

    if (lines.length === 0) continue;

    cards.push({
      id: card.id,
      party: card.party,
      label: labelFor(card.party, card.aiInterpreter),
      accent: card.party as Accent,
      stripe: stripeForDirection(card.direction, card.party),
      direction: card.direction,
      aiInterpreter: card.aiInterpreter,
      createdAt: card.createdAt,
      historical: card.historical,
      active: anyActive,
      finalized: allFinalized,
      segments: lines,
      // A card is, by construction, ONE speaker (a speaker change opens a new
      // card in the reducer). Consecutive utterances from that same speaker get a
      // line break at each real utterance boundary (see joinUtterances), so a
      // completed sentence starts a fresh line while an ASR-split continuation
      // stays on the same line.
      english: joinUtterances(englishParts),
      original: joinUtterances(originalParts),
      lang: cardLang,
      sourceLang: card.sourceLang || "",
      englishStreaming,
      originalStreaming
    });
  }
  return cards;
}

/** Filter card view models by a free-text search across both rows. */
export function filterCards(cards: CardView[], search: string): CardView[] {
  const q = (search || "").trim().toLowerCase();
  if (!q) return cards;
  return cards.filter((c) => `${c.english} ${c.original}`.toLowerCase().includes(q));
}
